import React, { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PlusIcon, ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import { useBuilding } from "../../context/BuildingContext";
import { bankAccountsApi, bankTransactionsApi } from "../../services/api";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import EmptyState from "../../components/common/EmptyState";
import PageHeader from "../../components/common/PageHeader";
import { formatCurrency, formatDate } from "../../utils/formatters";
import {
  BankAccount,
  BankAccountType,
  BankTransaction,
  BankTransactionType,
} from "@akfdev-com/shared/src/models";
import ImportTransactionsModal from "./ImportTransactionsModal";
import ExplainTransactionForm from "./ExplainTransactionForm";
import ExplainedTransactionSummary from "./ExplainedTransactionSummary";
import TransactionAttachments from "./TransactionAttachments";
import {
  ReconStatus,
  deriveReconStatus,
  countByStatus,
  computeRunningBalances,
  RECON_STATUS_LABEL,
} from "./bankStatementView";

const accountTypeLabels: Record<BankAccountType, string> = {
  [BankAccountType.CurrentAccount]: "Current Account",
  [BankAccountType.SavingsAccount]: "Savings Account",
  [BankAccountType.CashAccount]: "Cash Account",
  [BankAccountType.CreditCard]: "Credit Card",
  [BankAccountType.TrustAccount]: "Trust Account",
  [BankAccountType.Other]: "Other",
};

const transactionTypeLabels: Record<BankTransactionType, string> = {
  [BankTransactionType.InvoiceReceipt]: "Invoice Receipt",
  [BankTransactionType.Payment]: "Payment",
  [BankTransactionType.Transfer]: "Transfer",
  [BankTransactionType.Deposit]: "Deposit",
  [BankTransactionType.Withdrawal]: "Withdrawal",
  [BankTransactionType.BankCharge]: "Bank Charge",
  [BankTransactionType.Interest]: "Interest",
  [BankTransactionType.OpeningBalance]: "Opening Balance",
  [BankTransactionType.Other]: "Other",
};

const BankAccountDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentBuilding } = useBuilding();
  const buildingId =
    searchParams.get("buildingId") || currentBuilding?.id || "";

  const [showImportModal, setShowImportModal] = useState(false);
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | ReconStatus>("all");
  const [sortAsc, setSortAsc] = useState(false);

  const {
    data: account,
    isLoading: accountLoading,
    error: accountError,
  } = useQuery<BankAccount>({
    queryKey: ["bank-account", id, buildingId],
    queryFn: () => bankAccountsApi.get(id!, buildingId),
    enabled: !!id && !!buildingId,
  });

  const {
    data: transactions,
    isLoading: txLoading,
    error: txError,
    refetch: refetchTx,
  } = useQuery<BankTransaction[]>({
    queryKey: ["bank-transactions", id, buildingId],
    queryFn: () => bankTransactionsApi.getAll(buildingId, id!),
    enabled: !!id && !!buildingId,
  });

  // State for expanded transaction (must be before any early return)
  const [expandedTxId, setExpandedTxId] = useState<string | null>(null);

  if (accountLoading || txLoading) return <Spinner />;
  if (accountError)
    return <ErrorAlert message="Failed to load account details." />;
  if (!account) return <ErrorAlert message="Bank account not found." />;

  const allTransactions = transactions ?? [];

  // Inbox tab counts + running balances are derived from the FULL set (never
  // the filtered view) so counts and balances always reflect the whole account.
  const statusCounts = countByStatus(allTransactions);
  const runningBalances = computeRunningBalances(
    allTransactions,
    account.openingBalance ?? 0,
  );

  const displayTransactions = allTransactions
    .filter((tx) => !typeFilter || tx.type === typeFilter)
    .filter(
      (tx) => statusFilter === "all" || deriveReconStatus(tx) === statusFilter,
    )
    .sort((a, b) => {
      const cmp = a.date.localeCompare(b.date);
      return sortAsc ? cmp : -cmp;
    });

  // Current (closing) balance = opening balance + every signed transaction on
  // the account. All values are integer pence; convert at the display boundary.
  const closingBalance =
    (account.openingBalance ?? 0) +
    allTransactions.reduce((sum, tx) => sum + (tx.grossValue ?? 0), 0);

  return (
    <div>
      <PageHeader
        title={account.name}
        description={account.bankName ?? accountTypeLabels[account.accountType]}
        actions={
          <div className="flex gap-2">
            <button
              onClick={() => setShowImportModal(true)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <ArrowUpTrayIcon className="-ml-1 mr-2 h-4 w-4" />
              Import
            </button>
            <button
              onClick={() =>
                navigate(
                  `/dashboard/banking/${id}/transactions/new?buildingId=${buildingId}&bankAccountId=${id}&bankAccountName=${encodeURIComponent(
                    account.name,
                  )}`,
                )
              }
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
            >
              <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
              Add Transaction
            </button>
          </div>
        }
      />

      {/* Account summary */}
      <div className="mt-4 bg-white rounded-lg border border-gray-200 shadow-sm p-5">
        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Type
            </dt>
            <dd className="mt-1 text-sm font-semibold text-gray-900">
              {accountTypeLabels[account.accountType] ?? account.accountType}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Currency
            </dt>
            <dd className="mt-1 text-sm font-semibold text-gray-900">
              {account.currency}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Current Balance
            </dt>
            <dd
              className={`mt-1 text-lg font-bold ${
                closingBalance < 0 ? "text-red-600" : "text-gray-900"
              }`}
            >
              {formatCurrency(closingBalance / 100, account.currency)}
            </dd>
            <dd className="mt-0.5 text-xs text-gray-400">
              Opening{" "}
              {formatCurrency(
                (account.openingBalance ?? 0) / 100,
                account.currency,
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Status
            </dt>
            <dd className="mt-1">
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  account.isActive
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {account.isActive ? "Active" : "Inactive"}
              </span>
            </dd>
          </div>
        </dl>
        {account.description && (
          <p className="mt-3 text-sm text-gray-500">{account.description}</p>
        )}
      </div>

      {/* Transactions */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-gray-900">
            Transactions
          </h2>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-2 py-1"
          >
            <option value="">All Types</option>
            {Object.values(BankTransactionType).map((t) => (
              <option key={t} value={t}>
                {transactionTypeLabels[t]}
              </option>
            ))}
          </select>
        </div>

        {/* Status inbox tabs (Req 11) — derived from reconciled + allocatedAmount */}
        <div
          className="mb-3 flex flex-wrap gap-2"
          role="tablist"
          aria-label="Filter transactions by status"
        >
          {(
            [
              { key: "all", label: "All", count: allTransactions.length },
              {
                key: "needs",
                label: RECON_STATUS_LABEL.needs,
                count: statusCounts.needs,
              },
              {
                key: "partial",
                label: RECON_STATUS_LABEL.partial,
                count: statusCounts.partial,
              },
              {
                key: "matched",
                label: RECON_STATUS_LABEL.matched,
                count: statusCounts.matched,
              },
            ] as { key: "all" | ReconStatus; label: string; count: number }[]
          ).map((tab) => {
            const active = statusFilter === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setStatusFilter(tab.key)}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
                  active
                    ? "bg-gray-900 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {tab.label}
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                    active ? "bg-white/20 text-white" : "bg-white text-gray-500"
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {Boolean(txError) &&
          (typeof txError === "string" ? (
            <ErrorAlert message={txError} />
          ) : txError instanceof Error ? (
            <ErrorAlert message={txError.message} />
          ) : (
            <ErrorAlert message="Failed to load transactions." />
          ))}

        {!txError && displayTransactions.length === 0 ? (
          <EmptyState
            title="No transactions"
            description="Add a transaction manually or import from a spreadsheet."
          />
        ) : (
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Bank Account Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Description
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer select-none"
                      onClick={() => setSortAsc((v) => !v)}
                    >
                      Date {sortAsc ? "↑" : "↓"}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Type
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Gross Value
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Balance
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Status
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {displayTransactions.map((tx) => (
                    <React.Fragment key={tx.id}>
                      <tr
                        className={`hover:bg-gray-50 cursor-pointer ${
                          expandedTxId === tx.id ? "bg-primary-50" : ""
                        }`}
                        onClick={() =>
                          setExpandedTxId(expandedTxId === tx.id ? null : tx.id)
                        }
                      >
                        <td className="px-4 py-3 text-gray-700">
                          {tx.bankAccountName}
                        </td>
                        <td className="px-4 py-3 text-gray-900 font-medium">
                          {tx.description}
                        </td>
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                          {formatDate(tx.date)}
                        </td>
                        <td className="px-4 py-3 text-gray-500">
                          {transactionTypeLabels[tx.type] ?? tx.type}
                        </td>
                        <td
                          className={`px-4 py-3 text-right font-mono font-semibold ${
                            tx.grossValue >= 0
                              ? "text-green-700"
                              : "text-red-600"
                          }`}
                        >
                          {formatCurrency(
                            tx.grossValue / 100,
                            account.currency,
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-gray-700">
                          {runningBalances.has(tx.id)
                            ? formatCurrency(
                                (runningBalances.get(tx.id) ?? 0) / 100,
                                account.currency,
                              )
                            : "—"}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {(() => {
                            const status = deriveReconStatus(tx);
                            const badgeClass =
                              status === "matched"
                                ? "bg-green-100 text-green-800"
                                : status === "partial"
                                ? "bg-indigo-100 text-indigo-800"
                                : "bg-red-100 text-red-800";
                            return (
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badgeClass}`}
                              >
                                {RECON_STATUS_LABEL[status]}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            className="text-primary-600 hover:underline text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/dashboard/banking/${id}/transactions/${
                                  tx.id
                                }/edit?buildingId=${buildingId}&bankAccountId=${id}&bankAccountName=${encodeURIComponent(
                                  account.name,
                                )}`,
                              );
                            }}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                      {expandedTxId === tx.id && (
                        <tr className="bg-primary-50">
                          <td colSpan={8} className="px-6 py-4">
                            {tx.reconciled ? (
                              <ExplainedTransactionSummary
                                transaction={tx}
                                buildingId={buildingId}
                                currency={account.currency}
                                variant="inline"
                                fullPageHref={`/dashboard/banking/${id}/transactions/${tx.id}/explain?buildingId=${buildingId}&bankAccountId=${id}`}
                                onUnmatched={() => {
                                  setExpandedTxId(null);
                                  refetchTx();
                                }}
                              />
                            ) : (
                              <ExplainTransactionForm
                                transaction={tx}
                                buildingId={buildingId}
                                currency={account.currency}
                                variant="inline"
                                fullPageHref={`/dashboard/banking/${id}/transactions/${tx.id}/explain?buildingId=${buildingId}&bankAccountId=${id}`}
                                onReconciled={() => {
                                  setExpandedTxId(null);
                                  refetchTx();
                                }}
                              />
                            )}
                            <div className="mt-4">
                              <TransactionAttachments
                                transaction={tx}
                                buildingId={buildingId}
                                onChange={refetchTx}
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {showImportModal && (
        <ImportTransactionsModal
          buildingId={buildingId}
          bankAccountId={id!}
          bankAccountName={account.name}
          onClose={() => setShowImportModal(false)}
          onImported={() => {
            setShowImportModal(false);
            refetchTx();
          }}
        />
      )}
    </div>
  );
};

export default BankAccountDetails;
